import {
  isRecord,
  json,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

type Schemas = components['schemas']

export type ContentAudience = 'entity' | 'descendants'

export type ContentGroup = Schemas['ContentGroupResponse'] & {
  contentGroupId: string
  entityId: string
  parentId: string | null
  name: string
  sortOrder: number
  audience: ContentAudience
}

export type ContentGroupInput = { name: string; parentId: string | null; audience: ContentAudience }

export type ContentDocument = Schemas['ContentItemResponse'] & {
  contentItemId: string
  contentGroupId: string | null
  slug: string
  title: string
  description: string | null
  body: string
  status: 'draft' | 'published'
  sortOrder: number
  updatedAt: string
  hasDraft: boolean
  slugLocked: boolean
  revision: number
  audience: ContentAudience
}

export type ContentDocumentListItem = Schemas['ContentItemListResponse'] & {
  contentItemId: string
  contentGroupId: string | null
  slug: string
  title: string
  status: 'draft' | 'published'
  sortOrder: number
  updatedAt: string
  hasDraft: boolean
  audience: ContentAudience
}

export type ContentReaderTree = {
  groups: ContentGroup[]
  documents: ContentDocumentListItem[]
}

export type ContentSearchResult = Schemas['ContentSearchResultResponse'] & {
  contentItemId: string
  slug: string
  title: string
  snippet: string
}

export type ContentVersionSummary = Schemas['ContentVersionSummaryResponse'] & {
  contentVersionId: string
  title: string
  publishedAt: string
  isLive: boolean
}

export type ContentVersion = Schemas['ContentVersionResponse'] & {
  contentVersionId: string
  contentItemId: string
  contentGroupId: string | null
  title: string
  description: string | null
  body: string
  sortOrder: number
  publishedAt: string
  isLive: boolean
  missingMediaIds: string[]
}

export type ContentDocumentInput = {
  groupId: string | null
  slug: string
  title: string
  description: string | null
  body: string
  sortOrder: number
  audience: ContentAudience
}

export type ContentDocumentUpdate = ContentDocumentInput & { revision: number }

export type ContentMedia = Schemas['ContentMediaResponse'] & {
  contentMediaId: string
  url: string
  fileName: string | null
  contentType: string
  sizeBytes: number
}

export type ContentMediaUsage = 'published' | 'draft' | 'unused'

export type ContentMediaListItem = Schemas['ContentMediaListResponse'] & {
  contentMediaId: string
  url: string
  fileName: string | null
  contentType: string
  sizeBytes: number
  createdAt: string
  usage: ContentMediaUsage
  drafts: string[]
}

function decodeMedia(raw: unknown): ContentMedia {
  if (!isRecord(raw)) throw new Error('Malformed content media')
  return {
    contentMediaId: requireString(raw.contentMediaId, 'contentMediaId'),
    url: requireString(raw.url, 'url'),
    fileName: nullableString(raw.fileName, 'fileName'),
    contentType: requireString(raw.contentType, 'contentType'),
    sizeBytes: requireNumber(raw.sizeBytes, 'sizeBytes'),
  } satisfies ContentMedia
}

function decodeUsage(raw: unknown): ContentMediaUsage {
  const usage = requireString(raw, 'usage')
  if (usage === 'published' || usage === 'draft' || usage === 'unused') return usage
  throw new Error(`Unexpected content media usage "${usage}"`)
}

function decodeMediaListItem(raw: unknown): ContentMediaListItem {
  if (!isRecord(raw) || !Array.isArray(raw.drafts)) throw new Error('Malformed content media list item')
  return {
    ...decodeMedia(raw),
    createdAt: requireString(raw.createdAt, 'createdAt'),
    usage: decodeUsage(raw.usage),
    drafts: raw.drafts.map((draft) => requireString(draft, 'draft')),
  } satisfies ContentMediaListItem
}

function decodeStatus(raw: unknown): 'draft' | 'published' {
  const status = requireString(raw, 'status')
  if (status === 'draft' || status === 'published') return status
  throw new Error(`Unexpected content status "${status}"`)
}

function decodeAudience(raw: unknown): ContentAudience {
  const audience = requireString(raw, 'audience')
  if (audience === 'entity' || audience === 'descendants') return audience
  throw new Error(`Unexpected content audience "${audience}"`)
}

function decodeGroup(raw: unknown): ContentGroup {
  if (!isRecord(raw)) throw new Error('Malformed content group')
  return {
    contentGroupId: requireString(raw.contentGroupId, 'contentGroupId'),
    entityId: requireString(raw.entityId, 'entityId'),
    parentId: nullableString(raw.parentId, 'parentId'),
    name: requireString(raw.name, 'name'),
    sortOrder: requireNumber(raw.sortOrder, 'sortOrder'),
    audience: decodeAudience(raw.audience),
  } satisfies ContentGroup
}

function decodeDocument(raw: unknown): ContentDocument {
  if (!isRecord(raw)) throw new Error('Malformed content document')
  return {
    contentItemId: requireString(raw.contentItemId, 'contentItemId'),
    contentGroupId: nullableString(raw.contentGroupId, 'contentGroupId'),
    slug: requireString(raw.slug, 'slug'),
    title: requireString(raw.title, 'title'),
    description: nullableString(raw.description, 'description'),
    body: requireString(raw.body, 'body'),
    status: decodeStatus(raw.status),
    sortOrder: requireNumber(raw.sortOrder, 'sortOrder'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
    hasDraft: requireBoolean(raw.hasDraft, 'hasDraft'),
    slugLocked: requireBoolean(raw.slugLocked, 'slugLocked'),
    revision: requireNumber(raw.revision, 'revision'),
    audience: decodeAudience(raw.audience),
  } satisfies ContentDocument
}

function decodeDocumentListItem(raw: unknown): ContentDocumentListItem {
  if (!isRecord(raw)) throw new Error('Malformed content document list item')
  return {
    contentItemId: requireString(raw.contentItemId, 'contentItemId'),
    contentGroupId: nullableString(raw.contentGroupId, 'contentGroupId'),
    slug: requireString(raw.slug, 'slug'),
    title: requireString(raw.title, 'title'),
    status: decodeStatus(raw.status),
    sortOrder: requireNumber(raw.sortOrder, 'sortOrder'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
    hasDraft: requireBoolean(raw.hasDraft, 'hasDraft'),
    audience: decodeAudience(raw.audience),
  } satisfies ContentDocumentListItem
}

function decodeVersionSummary(raw: unknown): ContentVersionSummary {
  if (!isRecord(raw)) throw new Error('Malformed content version summary')
  return {
    contentVersionId: requireString(raw.contentVersionId, 'contentVersionId'),
    title: requireString(raw.title, 'title'),
    publishedAt: requireString(raw.publishedAt, 'publishedAt'),
    isLive: requireBoolean(raw.isLive, 'isLive'),
  } satisfies ContentVersionSummary
}

function decodeVersion(raw: unknown): ContentVersion {
  if (!isRecord(raw) || !Array.isArray(raw.missingMediaIds)) throw new Error('Malformed content version')
  return {
    ...decodeVersionSummary(raw),
    contentItemId: requireString(raw.contentItemId, 'contentItemId'),
    contentGroupId: nullableString(raw.contentGroupId, 'contentGroupId'),
    description: nullableString(raw.description, 'description'),
    body: requireString(raw.body, 'body'),
    sortOrder: requireNumber(raw.sortOrder, 'sortOrder'),
    missingMediaIds: raw.missingMediaIds.map((id) => requireString(id, 'missingMediaId')),
  } satisfies ContentVersion
}

function decodeReaderTree(raw: unknown): ContentReaderTree {
  if (!isRecord(raw) || !Array.isArray(raw.groups) || !Array.isArray(raw.documents)) {
    throw new Error('Malformed content reader tree')
  }
  return {
    groups: raw.groups.map(decodeGroup),
    documents: raw.documents.map(decodeDocumentListItem),
  } satisfies ContentReaderTree
}

function decodeSearchResult(raw: unknown): ContentSearchResult {
  if (!isRecord(raw)) throw new Error('Malformed content search result')
  return {
    contentItemId: requireString(raw.contentItemId, 'contentItemId'),
    slug: requireString(raw.slug, 'slug'),
    title: requireString(raw.title, 'title'),
    snippet: requireString(raw.snippet, 'snippet'),
  } satisfies ContentSearchResult
}

export const contentApi = {
  listGroups: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/content/groups`, (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed content group list')
      return raw.map(decodeGroup)
    }),
  getDocument: (entityId: string, documentId: string) =>
    request(`/api/v1/entities/${entityId}/content/documents/${documentId}`, decodeDocument),
  getReaderTree: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/content/docs`, decodeReaderTree),
  getReaderDocument: (entityId: string, slug: string) =>
    request(`/api/v1/entities/${entityId}/content/docs/${encodeURIComponent(slug)}`, decodeDocument),
  searchReaderDocuments: (entityId: string, query: string) =>
    request(`/api/v1/entities/${entityId}/content/docs:search?q=${encodeURIComponent(query)}`, (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed content search results')
      return raw.map(decodeSearchResult)
    }),
  createGroup: (entityId: string, input: ContentGroupInput) =>
    request(
      `/api/v1/entities/${entityId}/content/groups`,
      decodeGroup,
      json('POST', input),
    ),
  moveGroup: (entityId: string, groupId: string, direction: -1 | 1) =>
    request(
      `/api/v1/entities/${entityId}/content/groups/${groupId}:move`,
      decodeGroup,
      json('POST', { direction }),
    ),
  updateGroup: (entityId: string, groupId: string, input: ContentGroupInput) =>
    request(
      `/api/v1/entities/${entityId}/content/groups/${groupId}`,
      decodeGroup,
      json('PUT', input),
    ),
  deleteGroup: (entityId: string, groupId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/content/groups/${groupId}`, { method: 'DELETE' }),
  createDocument: (entityId: string, input: ContentDocumentInput) =>
    request(
      `/api/v1/entities/${entityId}/content/documents`,
      decodeDocument,
      json('POST', input),
    ),
  updateDocument: (entityId: string, documentId: string, input: ContentDocumentUpdate) =>
    request(
      `/api/v1/entities/${entityId}/content/documents/${documentId}`,
      decodeDocument,
      json('PUT', input),
    ),
  publishDocument: (entityId: string, documentId: string, revision: number) =>
    request(
      `/api/v1/entities/${entityId}/content/documents/${documentId}:publish`,
      decodeDocument,
      json('POST', { revision }),
    ),
  discardDraft: (entityId: string, documentId: string, revision: number) =>
    request(
      `/api/v1/entities/${entityId}/content/documents/${documentId}:discard-draft`,
      decodeDocument,
      json('POST', { revision }),
    ),
  unpublishDocument: (entityId: string, documentId: string) =>
    request(
      `/api/v1/entities/${entityId}/content/documents/${documentId}:unpublish`,
      decodeDocument,
      { method: 'POST' },
    ),
  listVersions: (entityId: string, documentId: string) =>
    request(`/api/v1/entities/${entityId}/content/documents/${documentId}/versions`, (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed content version list')
      return raw.map(decodeVersionSummary)
    }),
  getVersion: (entityId: string, documentId: string, versionId: string) =>
    request(`/api/v1/entities/${entityId}/content/documents/${documentId}/versions/${versionId}`, decodeVersion),
  restoreVersion: (entityId: string, documentId: string, versionId: string, input: { revision: number; overwriteDraft: boolean }) =>
    request(
      `/api/v1/entities/${entityId}/content/documents/${documentId}/versions/${versionId}:restore`,
      decodeDocument,
      json('POST', input),
    ),
  deleteDocument: (entityId: string, documentId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/content/documents/${documentId}`, { method: 'DELETE' }),
  listMedia: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/content/media`, (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed content media list')
      return raw.map(decodeMediaListItem)
    }),
  uploadMedia: (entityId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request(`/api/v1/entities/${entityId}/content/media`, decodeMedia, { method: 'POST', body: form })
  },
  deleteMedia: (entityId: string, mediaId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/content/media/${mediaId}`, { method: 'DELETE' }),
}
